/**
 * SegmentedControl - Apple-style segmented picker
 * Premium design with smooth sliding indicator
 * Features: haptic feedback, accessible focus states, consistent touch targets
 */

import { memo, useRef, useLayoutEffect, useState, useCallback } from 'react';
import { triggerHaptic } from '@/lib/haptics';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  size?: 'sm' | 'md';
  /** Accessible label for the segmented control group */
  'aria-label'?: string;
}

function SegmentedControlInner<T extends string>({
  options,
  value,
  onChange,
  className = '',
  size = 'md',
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const activeIndex = options.findIndex(o => o.value === value);

  // Calculate indicator position on mount and when selection changes
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const buttons = containerRef.current.querySelectorAll<HTMLButtonElement>('button');
    const activeButton = buttons[activeIndex];
    if (activeButton) {
      setIndicatorStyle({
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      });
    }
  }, [activeIndex, options]);

  const handleSelect = useCallback((optionValue: T) => {
    if (optionValue !== value) {
      triggerHaptic('light');
      onChange(optionValue);
    }
  }, [onChange, value]);

  // Size classes - 'md' ensures 44px minimum touch targets (Apple HIG)
  const sizeClasses = size === 'sm'
    ? 'min-h-[36px] text-label'
    : 'min-h-[44px] text-body-sm';

  const buttonPadding = size === 'sm' ? 'px-3' : 'px-4';

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      className={`relative inline-flex bg-gray-100 rounded-[14px] p-1 ${className}`}
    >
      {/* Sliding indicator - premium shadow with smooth easing */}
      <div
        className="absolute top-1 bottom-1 bg-white rounded-[10px]
                   shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_6px_rgba(0,0,0,0.04)]
                   motion-safe:transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
        aria-hidden="true"
      />

      {/* Option buttons - equal widths via flex-1 */}
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            role="tab"
            id={`tab-${option.value}`}
            aria-selected={isSelected}
            onClick={() => handleSelect(option.value)}
            className={`
              relative z-10 flex-1 ${buttonPadding} ${sizeClasses} font-semibold
              flex items-center justify-center rounded-[10px]
              motion-safe:transition-colors duration-150 touch-manipulation
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-inset
              ${isSelected
                ? 'text-gray-900'
                : 'text-gray-500 hover:text-gray-600 active:text-gray-700'
              }
            `}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export const SegmentedControl = memo(SegmentedControlInner) as typeof SegmentedControlInner;

